import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import type { Json } from "@/integrations/supabase/types";
import { 
  SIDEBAR_THEMES, 
  DEFAULT_SIDEBAR_THEME, 
  DEFAULT_LOGIN_THEME,
  DEFAULT_TEXT_MODE,
  getThemeById,
  type SidebarTheme,
  type LoginTheme 
} from "@/lib/sidebarThemes";
import { getGradientTextMode } from "@/lib/colorUtils";

interface SystemThemeSettings {
  sidebarTheme: SidebarTheme;
  loginTheme: LoginTheme;
  textMode: 'light' | 'dark';
  isLoading: boolean;
}

/**
 * Hook to fetch and manage system-wide theme settings.
 * These settings are defined by administrators and apply to ALL users.
 */
export const useSystemTheme = (): SystemThemeSettings & {
  updateSidebarTheme: (themeId: string) => Promise<void>;
  updateLoginTheme: (theme: LoginTheme) => Promise<void>;
  isUpdating: boolean;
} => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch sidebar theme setting
  const { data: sidebarThemeId, isLoading: isLoadingSidebar } = useQuery({
    queryKey: ["system-setting", "system_theme_sidebar"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_settings")
        .select("value")
        .eq("key", "system_theme_sidebar")
        .maybeSingle();
      
      if (error) {
        console.warn("Erro ao carregar tema da sidebar:", error);
        return null;
      }
      
      // Value is stored as JSON string, e.g., '"purple_blue"' or just 'purple_blue'
      const rawValue = data?.value;
      if (typeof rawValue === 'string') {
        return rawValue.replace(/^"|"$/g, ''); // Remove quotes if present
      }
      return null;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: false, // Don't retry on permission errors
  });

  // Fetch login theme setting
  const { data: loginThemeData, isLoading: isLoadingLogin } = useQuery({
    queryKey: ["system-setting", "system_theme_login"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_settings")
        .select("value")
        .eq("key", "system_theme_login")
        .maybeSingle();
      
      if (error) {
        console.warn("Erro ao carregar tema do login:", error);
        return null;
      }
      
      // Value is stored as JSON object
      const rawValue = data?.value;
      if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
        return rawValue as unknown as LoginTheme;
      }
      return null;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: false, // Don't retry on permission errors
  });

  // Mutation to update sidebar theme
  const updateSidebarMutation = useMutation({
    mutationFn: async (themeId: string) => {
      const { error } = await supabase
        .from("system_settings")
        .upsert({
          key: "system_theme_sidebar",
          value: `"${themeId}"` as unknown as Json,
          category: "appearance",
          description: "Tema de cores da sidebar para todos os usuários"
        }, { onConflict: 'key' });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-setting", "system_theme_sidebar"] });
      toast({
        title: "Tema atualizado",
        description: "O tema da sidebar foi atualizado para todos os usuários.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao atualizar tema",
        description: "Não foi possível atualizar o tema da sidebar.",
        variant: "destructive",
      });
    }
  });

  // Mutation to update login theme
  const updateLoginMutation = useMutation({
    mutationFn: async (theme: LoginTheme) => {
      const { error } = await supabase
        .from("system_settings")
        .upsert({
          key: "system_theme_login",
          value: theme as unknown as Json,
          category: "appearance",
          description: "Cores do gradiente da tela de login"
        }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-setting", "system_theme_login"] });
      toast({
        title: "Tema do login atualizado",
        description: "As cores da tela de login foram atualizadas.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao atualizar tema",
        description: "Não foi possível atualizar as cores do login.",
        variant: "destructive",
      });
    }
  });

  const currentTheme = getThemeById(sidebarThemeId);
  
  // Calculate text mode based on theme colors
  const textMode = currentTheme 
    ? getGradientTextMode(currentTheme.gradientFrom, currentTheme.gradientVia, currentTheme.gradientTo)
    : DEFAULT_TEXT_MODE;

  return {
    sidebarTheme: currentTheme,
    loginTheme: loginThemeData || DEFAULT_LOGIN_THEME,
    textMode,
    isLoading: isLoadingSidebar || isLoadingLogin,
    updateSidebarTheme: async (themeId: string) => {
      await updateSidebarMutation.mutateAsync(themeId);
    },
    updateLoginTheme: async (theme: LoginTheme) => {
      await updateLoginMutation.mutateAsync(theme);
    },
    isUpdating: updateSidebarMutation.isPending || updateLoginMutation.isPending,
  };
};

// Re-export theme utilities for convenience
export { SIDEBAR_THEMES, getThemeById, generateGradient } from "@/lib/sidebarThemes";
export type { SidebarTheme, LoginTheme } from "@/lib/sidebarThemes";
